/**
 * Zod schemas for validating critical API responses
 * Only applied to auth and settings endpoints where data integrity matters most
 */
import { z } from 'zod'

// ============================================================================
// User Schema (shared across auth responses)
// ============================================================================

export const UserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullish(),
  timezone: z.string().optional(),
  tier: z.string(),
  userPath: z.enum(['simple', 'byok']).optional(),
  createdAt: z.string(),
  emailVerified: z.boolean().optional(),
  signupCompletedAt: z.string().nullish(),
  setupWizardCompleted: z.boolean().optional(),
  verificationBannerDismissedAt: z.string().nullish(),
  googleId: z.string().nullish(),
  authProvider: z.string().nullish(),
  lastLoginAt: z.string().nullish(),
  profilePicture: z.string().nullish(),
})

// ============================================================================
// Auth Response Schemas
// ============================================================================

/** Login response: { success, data: { token, user }, error?, message? } */
/* Refresh token is delivered via httpOnly cookie set by the server (C4 fix). */
export const LoginResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    token: z.string(),
    user: UserSchema,
  }).optional(),
  error: z.string().optional(),
  message: z.string().optional(),
})

/** /api/auth/me response: { success, data: { user }, error? } */
export const MeResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    user: UserSchema,
  }).optional(),
  error: z.string().optional(),
})

// ============================================================================
// Settings Response Schema
// ============================================================================

export const UserSettingsSchema = z.object({
  defaultMode: z.string().optional(),
  preferredModels: z.array(z.string()).optional(),
  theme: z.string().optional(),
  showCostTracking: z.boolean().optional(),
  apiKeys: z.record(z.string(), z.boolean()).optional(),
}).passthrough()

export const SettingsResponseSchema = z.object({
  success: z.boolean(),
  data: UserSettingsSchema.optional(),
  error: z.string().optional(),
})

// ============================================================================
// Validation Helper
// ============================================================================

/**
 * Validates an API response against a Zod schema.
 * On failure, logs a warning and returns the raw data (graceful degradation).
 * This ensures validation issues surface in development without breaking production.
 */
export function validateResponse<T>(
  schema: z.ZodType<T>,
  data: unknown,
  label: string
): T {
  const result = schema.safeParse(data)
  if (!result.success) {
    console.warn(
      `[API] Response validation warning for ${label}:`,
      result.error.issues
    )
    // Graceful degradation: return raw data cast to T
    return data as T
  }
  return result.data
}
